import type {View} from '@sail/engine';
import {createViewConfig, view, assignProps, animate} from '@sail/engine';
import React, {useCallback, useLayoutEffect, useState} from 'react';
import {FocusScope} from '@sail/react-aria';
import type {MobileDisplay} from '../../utilStyles/sheet';
import {
  bottomSheetAnimation,
  bottomSheetStyles,
  fullscreenAnimation,
  fullscreenSheetStyles,
} from '../../utilStyles/sheet';
import {useNestedSheets} from './useNestedSheets';
import {Layer} from '../layers/Layer';
import {useUiConfig} from '../../providers/UiConfigProvider';
import {useOverlayIframe} from '../../iframe-overrides/OverlayOverrides';
import {Backdrop} from '../Backdrop';
import {css} from '../../css';
import {useSwipeDownToClose} from './useSwipeDownToClose';
import {
  BOTTOM_SHEET_ANIMATION_DURATION_SHORT,
  BOTTOM_SHEET_ANIMATION_DURATION_LONG,
  BOTTOM_SHEET_ANIMATION_EASING,
  BOTTOM_SHEET_HEIGHT_THRESHOLD_VH,
} from './BottomSheet.constants';
import {TextFieldConfig} from '../TextField';

type BottomSheetProps = View.IntrinsicElement<
  'div',
  {
    contentWrapperRef: React.RefObject<HTMLDivElement>;
    closeConfirmationDialog: React.ReactNode;
    closeConfirmationDialogHeight: number | null;
    requestClose?: () => void;
    mobileDisplay: MobileDisplay;
    subviews?: View.Subviews<{
      layer: typeof Layer;
      backdrop: typeof Backdrop;
    }>;
    FocusScopeWrapper?: React.FC<React.PropsWithChildren>;
    layerName?: string;
    /**
     * Modal bottom sheets block the rest of the page until dismissed.
     */
    modal: boolean;
    shouldAnimate: boolean;
    hasCloseConfirmationDialog?: boolean;
  }
>;

export const BottomSheetConfig = createViewConfig({
  props: {} as BottomSheetProps,
  name: 'BottomSheet',
});

/**
 * This is a Sail-internal component that is used in Overlays to render a bottom sheet on mobile/small screens.
 */
export const BottomSheet = BottomSheetConfig.createView(
  ({
    contentWrapperRef,
    children,
    requestClose,
    closeConfirmationDialog,
    closeConfirmationDialogHeight,
    layerName,
    mobileDisplay,
    modal,
    shouldAnimate,
    subviews,
    FocusScopeWrapper = React.Fragment,
    role = 'dialog',
    hasCloseConfirmationDialog,
    ...props
  }) => {
    const uiConfig = useUiConfig();
    const shouldApplyIframeOverrides = !!uiConfig?._unstable_requestIframe;
    const disableBottomSheetsSwipeDown =
      !!uiConfig?.disableBottomSheetsSwipeDown;

    const {sheetStackProvider, hasChildSheets, isFirstModalSheet} =
      useNestedSheets(modal);

    // Track the content wrapper element via state so that useSwipeDownToClose
    // re-runs when the element becomes available (e.g. after async iframe creation)
    const [contentElement, setContentElement] = useState<HTMLDivElement | null>(
      null,
    );

    const [backdropElement, setBackdropElement] =
      useState<HTMLDivElement | null>(null);

    const [animationDuration, setAnimationDuration] = useState(
      BOTTOM_SHEET_ANIMATION_DURATION_SHORT,
    );

    // Depending on the height of the content, we need to use a different animation duration
    useLayoutEffect(() => {
      if (!contentElement || mobileDisplay !== 'bottom-sheet') {
        return;
      }
      // eslint-disable-next-line @sail/ui-no-reflow
      const height = contentElement.getBoundingClientRect().height;
      // eslint-disable-next-line @sail/ui-no-reflow
      const vh = window.visualViewport?.height ?? window.innerHeight;

      setAnimationDuration(
        height > vh * BOTTOM_SHEET_HEIGHT_THRESHOLD_VH
          ? BOTTOM_SHEET_ANIMATION_DURATION_LONG
          : BOTTOM_SHEET_ANIMATION_DURATION_SHORT,
      );
    }, [contentElement, mobileDisplay]);

    const contentRef = useCallback(
      (node: HTMLDivElement | null) => {
        (
          contentWrapperRef as React.MutableRefObject<HTMLDivElement | null>
        ).current = node;
        setContentElement(node);
      },
      [contentWrapperRef],
    );

    const backdropRef = useCallback((node: HTMLDivElement | null) => {
      setBackdropElement(node);
    }, []);

    useSwipeDownToClose(
      disableBottomSheetsSwipeDown ? null : contentElement,
      // useSwipeDownToClose modifies the opacity of the backdrop, but only the first modal sheet should have a visible backdrop
      isFirstModalSheet ? backdropElement : null,
      requestClose,
      mobileDisplay,
      !!hasCloseConfirmationDialog,
    );

    function onKeyDown(e: React.KeyboardEvent<HTMLDivElement>) {
      if (e.key === 'Escape') {
        e.stopPropagation();
        requestClose?.();
      }
    }

    return (
      <Layer
        name={layerName}
        iosScrollLock={!modal}
        inherits={subviews.layer}
        uses={[
          ...(shouldApplyIframeOverrides
            ? [assignProps(useOverlayIframe)]
            : []),
          TextFieldConfig.customize({
            defaults: {
              size: 'large',
            },
          }),
        ]}
      >
        {modal ? (
          <Backdrop
            ref={backdropRef}
            inherits={subviews.backdrop}
            onClose={requestClose}
            opacity={isFirstModalSheet ? 1 : 0}
            uses={[
              animate.inout({
                duration: animationDuration,
                opacity: 0,
                easing: BOTTOM_SHEET_ANIMATION_EASING,
                preserveEasingOnReverse: true,
              }),
            ]}
          />
        ) : null}

        <FocusScopeWrapper>
          <FocusScope autoFocus restoreFocus contain={modal}>
            <view.div
              onKeyDown={onKeyDown}
              aria-modal={modal ? 'true' : undefined}
              {...props}
              role={role}
              ref={contentRef}
              uses={[
                mobileDisplay === 'bottom-sheet' ? sheetStackProvider : null,
                mobileDisplay === 'bottom-sheet' ? bottomSheetStyles : null,
                mobileDisplay === 'fullscreen-sheet'
                  ? fullscreenSheetStyles
                  : null,
                mobileDisplay === 'fullscreen-sheet'
                  ? css({
                      backgroundColor: 'surface',
                      position: 'fixed',
                    })
                  : null,
                closeConfirmationDialogHeight
                  ? css({
                      // CSS var used in mobileStyles for setting the minimum height of the bottom sheet
                      '--close-confirmation-dialog-height': `${closeConfirmationDialogHeight}px`,
                    })
                  : null,
                bottomSheetAnimation(
                  !shouldAnimate || mobileDisplay !== 'bottom-sheet',
                  animationDuration,
                  hasChildSheets,
                ),
                fullscreenAnimation(
                  !shouldAnimate || mobileDisplay !== 'fullscreen-sheet',
                ),
              ]}
            >
              {children}

              {closeConfirmationDialog}
            </view.div>
          </FocusScope>
        </FocusScopeWrapper>
      </Layer>
    );
  },
);
